import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { adminListTools, adminDeleteTool, adminResetToolRoi, adminReorderTools } from '../../services/toolsApi';

const AdminTools = () => {
  const { machineId } = useParams();
  const { password } = useAdmin();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListTools(password, machineId);
      setTools(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete tool "${name}"?`)) return;
    try {
      await adminDeleteTool(password, id);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleMove = async (idx, direction) => {
    const next = [...tools];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    // Reassign sort_order and slot to match new positions (1-based slot)
    const reindexed = next.map((t, i) => ({ ...t, sortOrder: i, slot: i + 1 }));
    setTools(reindexed);
    try {
      await adminReorderTools(password, reindexed.map((t) => ({ id: t.id, sort_order: t.sortOrder, slot: t.slot })));
    } catch (err) {
      alert(err.message);
      load(); // revert on error
    }
  };

  const handleResetRoi = async (id, name) => {
    if (!window.confirm(`Reset ROI for "${name}"? Earnings counter will restart from zero.`)) return;
    try {
      await adminResetToolRoi(password, id);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-3 border-dewalt border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin" className="text-sm text-gray-500 hover:text-gray-700 no-underline">&larr; Back to machines</Link>
          <h1 className="text-xl font-bold text-black mt-1">Tools &mdash; {machineId}</h1>
        </div>
        <Link
          to={`/admin/machines/${machineId}/tools/new`}
          className="px-4 py-2 bg-dewalt text-black text-sm font-bold rounded-lg no-underline hover:bg-dewalt-hover"
        >
          + New Tool
        </Link>
      </div>

      {tools.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No tools yet</p>
      ) : (
        <div className="space-y-3">
          {tools.map((t, idx) => (
            <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
              {/* Reorder buttons */}
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleMove(idx, 'up')}
                  disabled={idx === 0}
                  className="p-1 rounded text-gray-400 hover:text-gray-700 disabled:opacity-20 border-none bg-transparent cursor-pointer transition-colors"
                  aria-label="Move up"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleMove(idx, 'down')}
                  disabled={idx === tools.length - 1}
                  className="p-1 rounded text-gray-400 hover:text-gray-700 disabled:opacity-20 border-none bg-transparent cursor-pointer transition-colors"
                  aria-label="Move down"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
              <img src={t.image} alt="" className="w-14 h-14 object-contain rounded-lg bg-gray-50 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-black truncate">{t.name.uk}</div>
                <div className="text-sm text-gray-500">
                  {t.id} &middot; Slot {t.slot} &middot; {t.pricePer4Hours}/{t.pricePerDay}/{t.pricePerWeek}/{t.pricePerNight} UAH
                </div>
                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${t.available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                  {t.available ? 'Available' : 'Unavailable'}
                </span>
                {t.maintenance && (
                  <span className="inline-block mt-1 ml-1 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                    Maintenance
                  </span>
                )}
                {t.purchasePrice > 0 && (() => {
                  const pct = Math.min(100, Math.round(((t.roiEarned ?? 0) / t.purchasePrice) * 100));
                  return (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Принесено: {t.roiEarned ?? 0} / {t.purchasePrice} грн</span>
                        <span>{pct}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="bg-dewalt h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link
                  to={`/admin/machines/${machineId}/tools/${t.id}/edit`}
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg no-underline hover:bg-gray-200"
                >
                  Edit
                </Link>
                {t.purchasePrice > 0 && (
                  <button
                    onClick={() => handleResetRoi(t.id, t.name.uk)}
                    className="px-3 py-1.5 text-sm bg-amber-50 text-amber-600 rounded-lg border-none cursor-pointer hover:bg-amber-100"
                  >
                    Скинути ROI
                  </button>
                )}
                <button
                  onClick={() => handleDelete(t.id, t.name.uk)}
                  className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg border-none cursor-pointer hover:bg-red-100"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTools;
